import { useEffect, useMemo } from 'react'

import { abbreviateNumber, Heading, theme } from '@hhgtech/hhg-components'
import { Button, Text } from '@hhgtech/hhg-components/mantine'
import { Box, Flex } from '@mantine/core'
import dayjs from 'dayjs'
import { useIntl } from 'react-intl'

import { callApiNew } from 'api/axios'
import { PATHS } from 'api/paths'
import { getHelloSitesUrl } from 'api/utils/paths'
import { DetailStatistic } from 'components/atoms/detailStatistic'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { assetImagePath, DISPLAY_DATE_FORMAT_TEXT } from 'interfaces/constants'
import { TogetherTopic } from 'interfaces/types'

import { StyledCommunityBanner, StyledCommunityRevamp } from './index.styled'

type CommunityBannerProps = {
  title?: string
  topics: Array<TogetherTopic>
  dueDate: Date
  isRevamp?: boolean
}

const MINIMUM_MEMBERS_TO_DISPLAY = 1000

const Desktop = ({ topics, dueDate }: CommunityBannerProps) => {
  const { formatMessage: f } = useIntl()
  const topic = useMemo(() => topics[0], [topics])
  const { locale } = useRouterExtend()

  useEffect(() => {
    if (!topic.joined) {
      callApiNew(
        PATHS.TOGETHER.POST_TOGETHER_JOIN_TOPIC,
        {
          method: 'POST',
          data: {
            ids: [topic.id],
          },
        },
        'together',
      )
      topic.joined = true
    }
  }, [topic.joined])

  const { countMembers, countPosts, community } = topic

  return (
    <StyledCommunityBanner>
      <div className="community">
        <div className="thumbnail-header">
          <a
            href={`/community/${community.slug}/${topic.slug}`}
            data-event-category="Health Tool"
            data-event-action="Together Topic Click"
            data-event-label={`${getHelloSitesUrl(locale)}/community/${
              community.slug
            }/${topic.slug}`}
          >
            <img
              loading="lazy"
              className="thumbnail"
              src={
                topic.thumbnail || `${assetImagePath}/pregnancy-community.svg`
              }
              alt="thumbnail"
            />
          </a>
        </div>
        <div className="detail">
          <div className="detail-general">
            <div>
              <a
                href={`/community/${community.slug}/${topic.slug}`}
                className="pointer-event-child-none"
                data-event-category="Health Tool"
                data-event-action="Together Topic Click"
                data-event-label={`${getHelloSitesUrl(locale)}/community/${
                  community.slug
                }/${topic.slug}`}
              >
                <Heading
                  tag="h4"
                  fontWeight="bold"
                  color={theme.colors.gray800}
                  className="grow"
                >
                  {topic.name}
                </Heading>
              </a>
              <DetailStatistic
                postsCount={countPosts}
                membersCount={countMembers}
              />
            </div>

            <a
              href={`/community/${community.slug}/${topic.slug}`}
              data-event-category="Health Tool"
              data-event-action="Together Discussion Click"
              data-event-label={`${getHelloSitesUrl(locale)}/community/${
                community.slug
              }/${topic.slug}`}
              className="pointer-event-child-none"
            >
              <Button
                bg="#e0f8ee"
                size="md"
                leftIcon={
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path
                      d="M14.9852 4.49251C14.7904 4.49832 14.6054 4.57975 14.4696 4.71956L6.74988 12.4393L4.28015 9.96956C4.21104 9.89758 4.12827 9.84012 4.03667 9.80053C3.94508 9.76094 3.8465 9.74003 3.74672 9.73901C3.64694 9.73799 3.54796 9.7569 3.45558 9.79462C3.36319 9.83233 3.27926 9.8881 3.20871 9.95866C3.13815 10.0292 3.08238 10.1132 3.04466 10.2055C3.00694 10.2979 2.98804 10.3969 2.98905 10.4967C2.99007 10.5965 3.01098 10.695 3.05057 10.7866C3.09016 10.8782 3.14763 10.961 3.21961 11.0301L6.21961 14.0301C6.36026 14.1707 6.551 14.2497 6.74988 14.2497C6.94876 14.2497 7.1395 14.1707 7.28015 14.0301L15.5302 5.78011C15.6385 5.67476 15.7126 5.53913 15.7425 5.39099C15.7725 5.24285 15.757 5.08912 15.6981 4.94993C15.6392 4.81074 15.5396 4.69259 15.4124 4.61097C15.2852 4.52935 15.1363 4.48806 14.9852 4.49251Z"
                      fill="#008C50"
                    />
                  </svg>
                }
              >
                <Text c="#008c50">
                  {f({
                    id: 'together.embed.topicCard.joinDiscussion',
                    defaultMessage: 'Join Discussion',
                  })}
                </Text>
              </Button>
            </a>
          </div>
        </div>
      </div>

      <Text size="p4" color={theme.colors.gray800}>
        {f(
          {
            id: 'pregnancyTool.communityBanner.message',
            defaultMessage:
              'Looking for other moms who may give birth in <due-date></due-date>? Connect with <member></member> parents-to-be and share your stories in the <topicName></topicName>.',
          },
          {
            'due-date': function Component() {
              return (
                <Text
                  as="span"
                  size="p4"
                  color={theme.colors.gray800}
                  weight="semiBold"
                >
                  {dayjs(dueDate).format(DISPLAY_DATE_FORMAT_TEXT)}
                </Text>
              )
            },
            member: function Component() {
              return countMembers >= MINIMUM_MEMBERS_TO_DISPLAY ? (
                <Text
                  as="span"
                  size="p4"
                  color={theme.colors.gray800}
                  weight="semiBold"
                >
                  {abbreviateNumber(countMembers)}
                </Text>
              ) : null
            },
            topicName: function Component() {
              return (
                <Text
                  as="span"
                  size="p4"
                  color={theme.colors.gray800}
                  weight="semiBold"
                >
                  {topic.name}
                </Text>
              )
            },
          },
        )}
      </Text>
    </StyledCommunityBanner>
  )
}
const Mobile = ({ topics, dueDate }: CommunityBannerProps) => {
  const { formatMessage: f } = useIntl()
  const topic = useMemo(() => topics[0], [topics])
  const { locale } = useRouterExtend()
  useEffect(() => {
    if (!topic.joined) {
      callApiNew(
        PATHS.TOGETHER.POST_TOGETHER_JOIN_TOPIC,
        {
          method: 'POST',
          data: {
            ids: [topic.id],
          },
        },
        'together',
      )
      topic.joined = true
    }
  }, [topic.joined])

  const { countMembers, countPosts, community } = topic

  return (
    <StyledCommunityBanner>
      <div className="community">
        <div className="thumbnail-header">
          <a
            href={`/community/${community.slug}/${topic.slug}`}
            className="pointer-event-child-none"
            data-event-category="Health Tool"
            data-event-action="Together Topic Click"
            data-event-label={`${getHelloSitesUrl(locale)}/community/${
              community.slug
            }/${topic.slug}`}
          >
            <img
              loading="lazy"
              className="thumbnail"
              src={
                topic.thumbnail || `${assetImagePath}/pregnancy-community.svg`
              }
              alt="thumbnail"
            />
          </a>
        </div>
        <div className="detail">
          <div className="detail-general">
            <div>
              <a
                href={`/community/${community.slug}/${topic.slug}`}
                className="pointer-event-child-none"
                data-event-category="Health Tool"
                data-event-action="Together Topic Click"
                data-event-label={`${getHelloSitesUrl(locale)}/community/${
                  community.slug
                }/${topic.slug}`}
              >
                <Heading
                  tag="h4"
                  fontWeight="bold"
                  color={theme.colors.gray800}
                  className="grow"
                >
                  {topic.name}
                </Heading>
              </a>
              <DetailStatistic
                postsCount={countPosts}
                membersCount={countMembers}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="detail-message">
        <Text size="p4" color={theme.colors.gray800}>
          {f(
            {
              id: 'pregnancyTool.communityBanner.message',
              defaultMessage:
                'Looking for other moms who may give birth in <due-date></due-date>? Connect with <member></member> parents-to-be and share your stories in the <topicName></topicName>.',
            },
            {
              'due-date': function Component() {
                return (
                  <Text
                    as="span"
                    size="p4"
                    color={theme.colors.gray800}
                    weight="semiBold"
                  >
                    {dayjs(dueDate).format(DISPLAY_DATE_FORMAT_TEXT)}
                  </Text>
                )
              },
              member: function Component() {
                return countMembers >= MINIMUM_MEMBERS_TO_DISPLAY ? (
                  <Text
                    as="span"
                    size="p4"
                    color={theme.colors.gray800}
                    weight="semiBold"
                  >
                    {abbreviateNumber(countMembers)}
                  </Text>
                ) : null
              },
              topicName: function Component() {
                return (
                  <Text
                    as="span"
                    size="p4"
                    color={theme.colors.gray800}
                    weight="semiBold"
                  >
                    {topic.name}
                  </Text>
                )
              },
            },
          )}
        </Text>
      </div>
      <Box w="100%">
        <a
          href={`/community/${community.slug}/${topic.slug}`}
          className="pointer-event-child-none"
          data-event-category="Health Tool"
          data-event-action="Together Discussion Click"
          data-event-label={`${getHelloSitesUrl(locale)}/community/${
            community.slug
          }/${topic.slug}`}
        >
          <Button
            fullWidth
            size="md"
            bg="#e0f8ee"
            className="ask"
            leftIcon={
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path
                  d="M14.9852 4.49251C14.7904 4.49832 14.6054 4.57975 14.4696 4.71956L6.74988 12.4393L4.28015 9.96956C4.21104 9.89758 4.12827 9.84012 4.03667 9.80053C3.94508 9.76094 3.8465 9.74003 3.74672 9.73901C3.64694 9.73799 3.54796 9.7569 3.45558 9.79462C3.36319 9.83233 3.27926 9.8881 3.20871 9.95866C3.13815 10.0292 3.08238 10.1132 3.04466 10.2055C3.00694 10.2979 2.98804 10.3969 2.98905 10.4967C2.99007 10.5965 3.01098 10.695 3.05057 10.7866C3.09016 10.8782 3.14763 10.961 3.21961 11.0301L6.21961 14.0301C6.36026 14.1707 6.551 14.2497 6.74988 14.2497C6.94876 14.2497 7.1395 14.1707 7.28015 14.0301L15.5302 5.78011C15.6385 5.67476 15.7126 5.53913 15.7425 5.39099C15.7725 5.24285 15.757 5.08912 15.6981 4.94993C15.6392 4.81074 15.5396 4.69259 15.4124 4.61097C15.2852 4.52935 15.1363 4.48806 14.9852 4.49251Z"
                  fill="#008C50"
                />
              </svg>
            }
          >
            <Text c="#008c50">
              {f({
                id: 'together.embed.topicCard.joinDiscussion',
                defaultMessage: 'Join Discussion',
              })}
            </Text>
          </Button>
        </a>
      </Box>
    </StyledCommunityBanner>
  )
}
const Revamp = ({ topics, dueDate }: CommunityBannerProps) => {
  const isMobile = useIsMobile()
  const { formatMessage: f } = useIntl()
  const topic = useMemo(() => topics[0], [topics])
  const { locale } = useRouterExtend()
  useEffect(() => {
    if (!topic.joined) {
      callApiNew(
        PATHS.TOGETHER.POST_TOGETHER_JOIN_TOPIC,
        {
          method: 'POST',
          data: {
            ids: [topic.id],
          },
        },
        'together',
      )
      topic.joined = true
    }
  }, [topic.joined])

  const { countMembers, countPosts, community } = topic

  return (
    <StyledCommunityRevamp
      p={16}
      gap={16}
      bg={theme.colors.blue50}
      direction="column"
    >
      <Flex gap={16}>
        <a
          href={`/community/${community.slug}/${topic.slug}`}
          className="thumbnail pointer-event-child-none"
          data-event-category="Health Tool"
          data-event-action="Together Topic Click"
          data-event-label={`${getHelloSitesUrl(locale)}/community/${
            community.slug
          }/${topic.slug}`}
        >
          <img
            loading="lazy"
            src={topic.thumbnail || `${assetImagePath}/pregnancy-community.svg`}
            alt="thumbnail"
            width={48}
            height={48}
          />
        </a>
        <Flex gap={4} direction="column">
          <a
            href={`/community/${community.slug}/${topic.slug}`}
            className="pointer-event-child-none"
            data-event-category="Health Tool"
            data-event-action="Together Topic Click"
            data-event-label={`${getHelloSitesUrl(locale)}/community/${
              community.slug
            }/${topic.slug}`}
          >
            <Text
              size={isMobile ? 'h4' : 'h5'}
              as="p"
              weight="bold"
              color={theme.colors.gray800}
            >
              {topic.name}
            </Text>
          </a>
          <DetailStatistic
            postsCount={countPosts}
            membersCount={countMembers}
            size="small"
          />
        </Flex>
      </Flex>

      <Text size={isMobile ? 'p3' : 'p4'} color={theme.colors.gray800}>
        {f(
          {
            id: 'pregnancyTool.communityBanner.message',
            defaultMessage:
              'Looking for other moms who may give birth in <due-date></due-date>? Connect with <member></member> parents-to-be and share your stories in the <topicName></topicName>.',
          },
          {
            'due-date': function Component() {
              return (
                <Text
                  as="span"
                  size={isMobile ? 'p3' : 'p4'}
                  color={theme.colors.gray800}
                  weight="semiBold"
                >
                  {dayjs(dueDate).format(DISPLAY_DATE_FORMAT_TEXT)}
                </Text>
              )
            },
            member: function Component() {
              return countMembers >= MINIMUM_MEMBERS_TO_DISPLAY ? (
                <Text
                  as="span"
                  size={isMobile ? 'p3' : 'p4'}
                  color={theme.colors.gray800}
                  weight="semiBold"
                >
                  {abbreviateNumber(countMembers)}
                </Text>
              ) : null
            },
            topicName: function Component() {
              return (
                <Text
                  as="span"
                  size={isMobile ? 'p3' : 'p4'}
                  color={theme.colors.gray800}
                  weight="semiBold"
                >
                  {topic.name}
                </Text>
              )
            },
          },
        )}
      </Text>

      <a
        href={`/community/${community.slug}/${topic.slug}`}
        className="pointer-event-child-none"
        data-event-category="Health Tool"
        data-event-action="Due Date - Guide and Support - Join Community"
        data-event-label={`${getHelloSitesUrl(locale)}/community/${
          community.slug
        }/${topic.slug}`}
      >
        <Button
          fullWidth
          size="md"
          variant="secondary"
          // bg="#e0f8ee"
          leftIcon={
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path
                d="M14.9852 4.49251C14.7904 4.49832 14.6054 4.57975 14.4696 4.71956L6.74988 12.4393L4.28015 9.96956C4.21104 9.89758 4.12827 9.84012 4.03667 9.80053C3.94508 9.76094 3.8465 9.74003 3.74672 9.73901C3.64694 9.73799 3.54796 9.7569 3.45558 9.79462C3.36319 9.83233 3.27926 9.8881 3.20871 9.95866C3.13815 10.0292 3.08238 10.1132 3.04466 10.2055C3.00694 10.2979 2.98804 10.3969 2.98905 10.4967C2.99007 10.5965 3.01098 10.695 3.05057 10.7866C3.09016 10.8782 3.14763 10.961 3.21961 11.0301L6.21961 14.0301C6.36026 14.1707 6.551 14.2497 6.74988 14.2497C6.94876 14.2497 7.1395 14.1707 7.28015 14.0301L15.5302 5.78011C15.6385 5.67476 15.7126 5.53913 15.7425 5.39099C15.7725 5.24285 15.757 5.08912 15.6981 4.94993C15.6392 4.81074 15.5396 4.69259 15.4124 4.61097C15.2852 4.52935 15.1363 4.48806 14.9852 4.49251Z"
                fill={theme.colors.primaryBase}
              />
            </svg>
          }
        >
          <Text c={theme.colors.primaryBase}>
            {f({
              id: 'together.embed.topicCard.joinDiscussion',
              defaultMessage: 'Join Discussion',
            })}
          </Text>
        </Button>
      </a>
    </StyledCommunityRevamp>
  )
}

export const CommunityBanner = (props: CommunityBannerProps) => {
  const isMobile = useIsMobile()

  if (props.isRevamp) {
    return <Revamp {...props} />
  }

  return isMobile ? <Mobile {...props} /> : <Desktop {...props} />
}
